import type { ReactNode } from "react";
import { alpha, Button, ButtonGroup, Stack } from "@mui/material";

export type DocumentAction = {
  label: string;
  icon?: ReactNode;
  href?: string;
  target?: "_blank";
  disabled?: boolean;
  onClick?: () => void;
};

export function SegmentedButtons({
  value,
  options,
  onChange,
  iconOnly = false,
}: {
  value: string;
  options: { value: string; label: string; icon?: ReactNode }[];
  onChange: (value: string) => void;
  /** Renders each option's icon instead of its label, for space-tight layouts. */
  iconOnly?: boolean;
}) {
  return (
    <Stack
      direction="row"
      sx={{
        p: 0.25,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        backgroundColor: "background.neutral",
      }}
    >
      {options.map((option) => {
        const selected = value === option.value;

        const showIcon = iconOnly && option.icon;

        return (
          <Button
            key={option.value}
            variant="text"
            size="small"
            onClick={() => onChange(option.value)}
            // The label still needs to reach assistive tech once it is not on screen.
            aria-label={showIcon ? option.label : undefined}
            title={showIcon ? option.label : undefined}
            sx={{
              minWidth: showIcon ? 42 : 64,
              px: showIcon ? 0 : undefined,
              height: 26,
              py: 0,
              minHeight: 0,
              lineHeight: 1.35,
              borderRadius: 0.75,
              color: selected ? "primary.dark" : "text.secondary",
              backgroundColor: selected ? "common.white" : "transparent",
              boxShadow: selected ? "0 1px 3px rgba(22, 28, 36, 0.12)" : "none",
              fontSize: 11,
              fontWeight: 500,
              letterSpacing: 0.4,
              textTransform: "uppercase",
              "&:hover": {
                backgroundColor: selected ? "common.white" : "action.hover",
              },
            }}
          >
            {showIcon ? option.icon : option.label}
          </Button>
        );
      })}
    </Stack>
  );
}

export function ActionButtonGroup({
  actions,
  align = "left",
  onAction,
}: {
  actions: Array<string | DocumentAction>;
  align?: "left" | "right";
  onAction?: (label: string) => void;
}) {
  return (
    <Stack
      direction="row"
      justifyContent={{
        xs: "flex-start",
        xl: align === "right" ? "flex-end" : "flex-start",
      }}
    >
      <ButtonGroup
        variant="outlined"
        size="small"
        sx={{
          "& .MuiButton-root": {
            height: 26,
            minWidth: "auto",
            px: 1,
            py: 0.5,
            border: "1px solid",
            borderColor: (theme) => alpha(theme.palette.grey[500], 0.18),
            backgroundColor: (theme) => alpha(theme.palette.grey[500], 0.06),
            fontSize: 12,
            fontWeight: 500,
            textTransform: "none",
            "&:hover": {
              color: "primary.dark",
              borderColor: (theme) => alpha(theme.palette.primary.main, 0.32),
              backgroundColor: (theme) =>
                alpha(theme.palette.primary.main, 0.06),
            },
            "& .MuiButton-startIcon": {
              mr: 0.5,
              ml: 0,
            },
          },
        }}
      >
        {actions.map((action, index) => {
          const config =
            typeof action === "string" ? { label: action } : action;
          const linkProps = config.href
            ? {
                component: "a" as const,
                href: config.href,
                target: config.target,
                rel:
                  config.target === "_blank"
                    ? "noopener noreferrer"
                    : undefined,
              }
            : {};

          return (
            <Button
              key={config.label}
              disabled={config.disabled}
              startIcon={config.icon}
              onClick={() => {
                config.onClick?.();
                onAction?.(config.label);
              }}
              {...linkProps}
              sx={{
                color: index === 0 ? "primary.dark" : "text.secondary",
              }}
            >
              {config.label}
            </Button>
          );
        })}
      </ButtonGroup>
    </Stack>
  );
}
